import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';
import InputAdornment from '@mui/material/InputAdornment';
import { alpha, SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { useSwiftCodeLookupQuery } from '@/api/v1';
import { FormCheckbox } from '@/components/atoms/FormCheckbox';
import { AddressFields } from '@/components/organisms/AddressFields';
import { ControllerWithError, ControllerWithErrorParam } from '@/forms/ControllerWithError';
import { CountryCity, useCountryCities } from '@/hooks/useCountryCities';
import { FormWithErrorsRegister } from '@/hooks/useFormWithErrors';
import { useTranslator } from '@/hooks/useTranslator';
import type { paymentMethodFiatSchema } from '@/schemas/paymentMethod';
import { isValidSwiftCode } from '@/utility/swift';

type PaymentMethodForm = typeof paymentMethodFiatSchema.infer;

interface Props {
  readonly register: FormWithErrorsRegister<PaymentMethodForm>;
  readonly control: Control<PaymentMethodForm>;
  readonly watch: UseFormWatch<PaymentMethodForm>;
  readonly setValue: UseFormSetValue<PaymentMethodForm>;
  readonly onIbanBlur?: () => Promise<void>;
  readonly onIbanChange?: () => void;
  readonly onSwiftCodeChange?: () => void;
  readonly isIbanValidating?: boolean;
  readonly swiftLookupMessage?: string | null;
}

export const SwiftAccountDetailsFields: React.FC<Props> = ({
  register,
  watch,
  control,
  setValue,
  onIbanBlur,
  onIbanChange,
  onSwiftCodeChange,
  isIbanValidating = false,
  swiftLookupMessage,
}: Props): React.ReactElement => {
  const tr = useTranslator();
  const [animationKey, setAnimationKey] = React.useState<number>(0);
  const swiftFieldRef = React.useRef<HTMLDivElement>(null);
  const [country, state, swiftCode, iban] = watch([
    'country',
    'address.state',
    'swiftCode',
    'iban',
  ]);
  const swiftCodeField = register({ name: 'swiftCode', label: tr('SWIFT_CODE') });
  const ibanField = register({ name: 'iban', label: tr('IBAN_ACCOUNT_NUMBER') });
  const showSwiftLookupBadge = Boolean(swiftLookupMessage) && !swiftCodeField.error;
  const shouldLookupSwiftCode = isValidSwiftCode(swiftCode);

  const cities = useCountryCities(country);
  const {
    data: bankData,
    isError: isSwiftLookupError,
    isLoading: isSwiftLookupLoading,
  } = useSwiftCodeLookupQuery(swiftCode, {
    skip: !shouldLookupSwiftCode,
  });
  const isLookupInProgress = isSwiftLookupLoading || isIbanValidating;

  const swiftLookupError =
    swiftCode && !shouldLookupSwiftCode
      ? tr('SWIFT_CODE_INVALID')
      : shouldLookupSwiftCode && isSwiftLookupError
        ? tr('SWIFT_CODE_NOT_FOUND')
        : undefined;

  React.useEffect((): void => {
    if (showSwiftLookupBadge) {
      setAnimationKey((previous: number): number => previous + 1);

      // Restart the flash animation on the TextField without remounting it.
      // Removing and re-adding the class after a forced reflow restarts CSS animations.
      const el = swiftFieldRef.current;
      if (el) {
        el.classList.remove('swift-flash-active');
        void el.offsetHeight;
        el.classList.add('swift-flash-active');
      }
    }
  }, [showSwiftLookupBadge, swiftLookupMessage]);

  React.useEffect((): void => {
    if (bankData) {
      const { name, address, cityName, postcode } = bankData.data;

      setValue('bankName', name ?? '', { shouldTouch: true, shouldValidate: true });
      setValue('address.street', address ?? '', { shouldTouch: true, shouldValidate: true });
      setValue(
        'address.state',
        cities.find((city: CountryCity): boolean => {
          const { name } = city;

          return name.toLowerCase() === cityName.toLowerCase();
        })?.state ?? null,
        { shouldTouch: true, shouldValidate: true },
      );
      setValue('address.complement', '', { shouldTouch: true, shouldValidate: true });
      setValue('address.city', cityName ?? '', { shouldTouch: true, shouldValidate: true });
      setValue('address.zipCode', postcode ?? '', { shouldTouch: true, shouldValidate: true });
    }
  }, [bankData, cities, country, setValue]);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box>
        <Typography variant="body2" color="text.primary">
          {tr('BANK_ACCOUNT_INFORMATION')}
        </Typography>
      </Box>
      <Box display="flex" gap={2}>
        <Box
          ref={swiftFieldRef}
          flex={1}
          minWidth={0}
          position="relative"
          sx={swiftFieldFlashStyle}>
          <Fade in={showSwiftLookupBadge} timeout={220} unmountOnExit={true}>
            <Box sx={badgeContainerAnimation}>
              <Chip
                key={animationKey}
                label={swiftLookupMessage}
                size="small"
                color="info"
                variant="outlined"
                sx={badgePulseStyle}
              />
            </Box>
          </Fade>
          <TextField
            fullWidth={true}
            {...swiftCodeField}
            error={!!swiftLookupError || swiftCodeField.error}
            helperText={swiftLookupError || swiftCodeField.helperText}
            slotProps={{
              inputLabel: { shrink: Boolean(swiftCode) },
              input: {
                readOnly: isLookupInProgress,
                ...(isSwiftLookupLoading && {
                  endAdornment: (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ),
                }),
              },
            }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              setValue('swiftCode', e.target.value.replace(/\s/g, ''), {
                shouldTouch: true,
                shouldValidate: true,
              });
              onSwiftCodeChange?.();
            }}
          />
        </Box>
        <Box flex={1} minWidth={0}>
          <TextField
            fullWidth={true}
            {...ibanField}
            slotProps={{
              inputLabel: { shrink: Boolean(iban) },
              input: {
                readOnly: isLookupInProgress,
                ...(isIbanValidating && {
                  endAdornment: (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ),
                }),
              },
            }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              setValue('iban', e.target.value.replace(/\s/g, ''), {
                shouldTouch: true,
                shouldValidate: true,
              });
              onIbanChange?.();
            }}
            onBlur={(e: React.FocusEvent<HTMLInputElement>): void => {
              ibanField.onBlur(e);
              void onIbanBlur?.();
            }}
          />
        </Box>
      </Box>
      <ControllerWithError
        control={control}
        name="bankName"
        label={tr('BANK_NAME')}
        render={({
          field,
        }: ControllerWithErrorParam<PaymentMethodForm, 'bankName'>): React.ReactElement => (
          <TextField
            fullWidth={true}
            {...field}
            slotProps={{ input: { readOnly: isLookupInProgress } }}
          />
        )}
      />

      <AddressFields<PaymentMethodForm>
        country={country}
        state={state}
        control={control}
        setValue={setValue}
        readOnly={isLookupInProgress}
      />

      <FormCheckbox {...register({ name: 'isPobo', label: tr('IS_POBO') })} />
    </Box>
  );
};

const badgeContainerAnimation: SxProps = {
  position: 'absolute',
  right: 12,
  top: -12,
  zIndex: 1,
  animation: 'badgeSlideIn 220ms ease-out',
  '@keyframes badgeSlideIn': {
    '0%': { transform: 'translateY(-4px)' },
    '100%': { transform: 'translateY(0)' },
  },
};

// Animations re-trigger via React's `key` prop: when animationKey increments,
// React remounts the element, which restarts the CSS animation automatically.
const badgePulseStyle: SxProps = {
  backgroundColor: 'background.paper',
  animation: 'badgePulse 420ms ease-out',
  '@keyframes badgePulse': {
    '0%': { transform: 'scale(1)' },
    '45%': { transform: 'scale(1.05)' },
    '100%': { transform: 'scale(1)' },
  },
};

// Animations are triggered by toggling the `.swift-flash-active` class on the
// TextField root via a ref. This avoids remounting the component (which a
// `key`-based approach would do, destroying focus and internal state).
const swiftFieldFlashStyle: SxProps<Theme> = (theme: Theme): SystemStyleObject => ({
  '&.swift-flash-active .MuiOutlinedInput-root': {
    animation: 'swiftFieldFlash 520ms ease-out',
    '@keyframes swiftFieldFlash': {
      '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.info.main, 0.45)}` },
      '45%': { boxShadow: `0 0 0 4px ${alpha(theme.palette.info.main, 0.2)}` },
      '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.info.main, 0)}` },
    },
  },
  '&.swift-flash-active .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
    animation: 'swiftBorderFlash 520ms ease-out',
    '@keyframes swiftBorderFlash': {
      '0%': { borderColor: alpha(theme.palette.info.main, 0.95) },
      '100%': { borderColor: alpha(theme.palette.info.main, 0.35) },
    },
  },
});
